import React, { useState, useEffect } from 'react';
import { MdEmail } from 'react-icons/md';
import CustomModal from './CustomModal';
import axios from '../axios';
import toast from 'react-hot-toast';

const EmailVerificationModal = ({ isOpen, setModalOpen, onClose }) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [error, setError] = useState('');

  // Get user email from localStorage
  const userEmail = localStorage.getItem('user_email') || 'your email';

  // Auto-resend OTP when modal opens
  useEffect(() => {
    if (isOpen) {
      handleResendOTP();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOtpChange = (e, index) => {
    if (isNaN(e.target.value)) return false;

    setOtp([...otp.map((data, indx) => (indx === index ? e.target.value : data))]);

    if (e.target.value && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData('text');
    if (/^\d{6}$/.test(pasteData)) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');
    
    try {
      // Call the resend OTP API (using the same endpoint as signup/signin)
      const response = await axios.put('auth/register/', { 
        email: userEmail 
      });
      
      if (response.status === 200) {
        toast.success('OTP sent successfully!');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('').replace(/,/g, '');
    
    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyLoading(true);
    setError('');
    
    try {
      // Call the verify OTP API (using the same endpoint as signup)
      const response = await axios.post('auth/verify_token/', {
        user_email: userEmail,
        token: otpString
      });
      
      if (response.status === 200) {
        // Update localStorage to mark email as verified
        localStorage.setItem('email_verified', 'true');
        
        // Show success toast
        toast.success('Email verified successfully!');
        
        // Close the modal
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
          setModalOpen();
        }, 1000);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Invalid OTP. Please check and try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setModalOpen();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      setModalOpen={setModalOpen}
      heading="Verify Your Email"
      modalWidth="500px"
      onClose={handleClose}
    >
      <div className="text-center mb-6">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <MdEmail className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          Enter Verification Code
        </h3>
        <p className="text-gray-600 mb-4">
          We've sent a 6-digit verification code to:
        </p>
        <p className="text-blue-600 font-medium break-all">{userEmail}</p>
      </div>

      <div className="space-y-4">
        {/* OTP Input Fields */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 text-center">
            Enter the 6-digit code
          </label>
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                style={{ fontSize: '20px' }}
              />
            ))}
          </div>
          {error && (
            <p className="text-red-600 text-sm text-center">{error}</p>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">What to do next:</h4>
          <ol className="text-sm text-blue-700 space-y-1">
            <li>1. Check your email inbox (and spam folder)</li>
            <li>2. Enter the 6-digit verification code above</li>
            <li>3. Click "Verify Code" to complete verification</li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleVerifyOTP}
            disabled={verifyLoading || otp.join('').replace(/,/g, '').length !== 6}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {verifyLoading ? 'Verifying...' : 'Verify Code'}
          </button>
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>

        {/* Resend OTP */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Didn't receive the code?{' '}
            <button
              onClick={handleResendOTP}
              disabled={resendLoading}
              className="text-blue-600 hover:text-blue-700 font-medium underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading ? 'Sending...' : 'Resend Code'}
            </button>
          </p>
        </div>
      </div>
    </CustomModal>
  );
};

export default EmailVerificationModal;
